/* Translation lookup. React-free so the dictionaries and the formatter are unit-tested directly. */
import { LANGS, LANG_NAMES, type Lang } from '@pathabroad/core/browser';
import { en, type Dict, type MessageKey } from '../i18n/en';
import { ar } from '../i18n/ar';
import { hi } from '../i18n/hi';
import { idn } from '../i18n/id';

export type { MessageKey };

/* the languages for a select, each named in its own script */
export const LANG_OPTIONS = LANGS.map(l => ({ value: l, label: LANG_NAMES[l], lang: l }));
export const DICTS: Record<Lang, Dict> = { en, ar, hi, id: idn };
export type Vars = Record<string, string | number>;

export function translate(lang: Lang, key: MessageKey, vars?: Vars): string {
  const s: string = DICTS[lang][key] ?? DICTS.en[key] ?? key;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

/* Splits a message on its {slots} so a page can put links or other elements in them, e.g. "agree to the {terms}". */
export function slots(template: string): Array<{ text: string } | { slot: string }> {
  const out: Array<{ text: string } | { slot: string }> = [];
  let last = 0;
  for (const m of template.matchAll(/\{(\w+)\}/g)) {
    if (m.index! > last) out.push({ text: template.slice(last, m.index) });
    out.push({ slot: m[1] });
    last = m.index! + m[0].length;
  }
  if (last < template.length) out.push({ text: template.slice(last) });
  return out;
}

export const placeholders = (s: string) => [...s.matchAll(/\{(\w+)\}/g)].map(m => m[1]).sort();

/* "12 months" / "12 شهرًا" / "12 महीने" / "12 bulan": Arabic counts change form with the number; Hindi has a singular and a
   plural; Indonesian doesn't change */
export function monthsText(n: number, lang: Lang): string {
  if (lang === 'en') return n === 1 ? '1 month' : `${n} months`;
  if (lang === 'hi') return n === 1 ? '1 महीना' : `${n} महीने`;
  if (lang === 'id') return `${n} bulan`;
  const rule = new Intl.PluralRules('ar').select(n);
  if (rule === 'one') return 'شهر واحد';
  if (rule === 'two') return 'شهران';
  if (rule === 'few') return `${n} أشهر`;
  if (rule === 'many') return `${n} شهرًا`;
  return `${n} شهر`;
}

/* first visit: an earlier choice in this browser, else the first of the browser's languages that we have */
export function initialLang(stored: Lang | null, navigatorLangs: readonly string[] = []): Lang {
  if (stored) return stored;
  for (const l of navigatorLangs) {
    if (/^ar\b/i.test(l)) return 'ar';
    if (/^hi\b/i.test(l)) return 'hi';
    if (/^(id|in)\b/i.test(l)) return 'id';   /* "in" is the old code some Android versions still send */
    if (/^en\b/i.test(l)) return 'en';
  }
  return 'en';
}
